/**
 * Intelligent Inventory API client.
 *
 * Thin wrapper over the new microservices routed via API Gateway. All requests
 * include the Cognito JWT pulled from session/localStorage (same fallback
 * pattern used by apiRequest in apiRouting.ts). Each function returns parsed
 * JSON on 2xx or throws an InventoryApiError carrying the backend error code
 * on non-2xx.
 */
import { API_ENDPOINTS } from '../config/apiRouting';

export class InventoryApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'InventoryApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getAuthToken(): string | null {
  return (
    sessionStorage.getItem('idToken') ||
    localStorage.getItem('idToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('authToken')
  );
}

async function request<T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });
  const text = await response.text();
  let body: any = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const code = body && typeof body === 'object' ? body.code || body.error : undefined;
    const message =
      (body && typeof body === 'object' && (body.message || body.error)) ||
      `Request failed with status ${response.status}`;
    throw new InventoryApiError(message, response.status, code, body);
  }

  return body as T;
}

// ---- Stock / counts / snapshots ---------------------------------------------
export const fetchStockLevels = (storeId: string) =>
  request(API_ENDPOINTS.inventoryStockLevels(storeId));

export const submitCount = (storeId: string, body: any) =>
  request(API_ENDPOINTS.inventoryCount(storeId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchSnapshots = (storeId: string) =>
  request(API_ENDPOINTS.inventorySnapshots(storeId));

export const createSnapshot = (storeId: string, body: any) =>
  request(API_ENDPOINTS.inventorySnapshots(storeId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateInventoryConfig = (storeId: string, body: any) =>
  request(API_ENDPOINTS.inventoryConfigUpdate(storeId), {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const logWaste = (storeId: string, body: any) =>
  request(API_ENDPOINTS.inventoryWaste(storeId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchWasteReport = (storeId: string) =>
  request(API_ENDPOINTS.inventoryWasteReport(storeId));

// ---- Recipes ----------------------------------------------------------------
export const fetchRecipes = (enterpriseId: string) =>
  request(API_ENDPOINTS.recipes(enterpriseId));

export const fetchRecipe = (enterpriseId: string, recipeId: string) =>
  request(API_ENDPOINTS.recipe(enterpriseId, recipeId));

export const createRecipe = (enterpriseId: string, body: any) =>
  request(API_ENDPOINTS.recipes(enterpriseId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateRecipe = (enterpriseId: string, recipeId: string, body: any) =>
  request(API_ENDPOINTS.recipe(enterpriseId, recipeId), {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const deleteRecipe = (enterpriseId: string, recipeId: string) =>
  request(API_ENDPOINTS.recipe(enterpriseId, recipeId), {
    method: 'DELETE',
  });

export const explodeRecipe = (enterpriseId: string, recipeId: string, servings: number) =>
  request(API_ENDPOINTS.recipeExplode(enterpriseId, recipeId), {
    method: 'POST',
    body: JSON.stringify({ servings }),
  });

// ---- Forecast ---------------------------------------------------------------
export const fetchForecast = (storeId: string) =>
  request(API_ENDPOINTS.inventoryForecast(storeId));

export const refreshForecast = (storeId: string) =>
  request(API_ENDPOINTS.inventoryForecastRefresh(storeId), {
    method: 'POST',
    body: JSON.stringify({}),
  });

// ---- Purchase orders --------------------------------------------------------
export const fetchPurchaseOrders = (storeId: string, status?: string) => {
  const base = API_ENDPOINTS.purchaseOrders(storeId);
  const url = status ? `${base}?status=${encodeURIComponent(status)}` : base;
  return request(url);
};

export const generatePurchaseOrder = (storeId: string, body: any = {}) =>
  request(API_ENDPOINTS.generatePurchaseOrder(storeId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const approvePO = (storeId: string, poId: string) =>
  request(API_ENDPOINTS.approvePurchaseOrder(storeId, poId), {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const submitPO = (storeId: string, poId: string) =>
  request(API_ENDPOINTS.submitPurchaseOrder(storeId, poId), {
    method: 'POST',
    body: JSON.stringify({}),
  });

// ---- Receiving --------------------------------------------------------------
export const logReceiving = (storeId: string, body: any) =>
  request(API_ENDPOINTS.inventoryReceiving(storeId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchReceivingList = (storeId: string) =>
  request(API_ENDPOINTS.inventoryReceiving(storeId));

export const fetchReceivingDelivery = (storeId: string, deliveryId: string) =>
  request(API_ENDPOINTS.inventoryReceivingDelivery(storeId, deliveryId));

// ---- Invoices ---------------------------------------------------------------
export const scanInvoice = (storeId: string, imageKey: string) =>
  request(API_ENDPOINTS.invoiceScan(storeId), {
    method: 'POST',
    body: JSON.stringify({ imageKey }),
  });

export const fetchInvoiceMatch = (storeId: string, invoiceId: string) =>
  request(API_ENDPOINTS.invoiceMatch(storeId, invoiceId));

// ---- Suppliers --------------------------------------------------------------
export const fetchSuppliers = (enterpriseId: string) =>
  request(API_ENDPOINTS.suppliers(enterpriseId));

export const fetchSupplier = (enterpriseId: string, supplierId: string) =>
  request(API_ENDPOINTS.supplier(enterpriseId, supplierId));

export const fetchSupplierPerformance = (supplierId: string) =>
  request(API_ENDPOINTS.supplierPerformance(supplierId));

// ---- Network alerts ---------------------------------------------------------
export const fetchNetworkAlerts = () =>
  request(API_ENDPOINTS.networkAlerts());

// ---- Waste analytics --------------------------------------------------------
export const fetchWasteAnalysis = (storeId: string) =>
  request(API_ENDPOINTS.wasteAnalysis(storeId));

// ---- Vision -----------------------------------------------------------------
export const submitVisionCount = (body: any) =>
  request(API_ENDPOINTS.visionCount(), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const submitVisionReceiving = (body: any) =>
  request(API_ENDPOINTS.visionReceiving(), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const submitVisionFeedback = (body: any) =>
  request(API_ENDPOINTS.visionFeedback(), {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchVisionZones = (storeId: string) =>
  request(API_ENDPOINTS.visionZones(storeId));

export const saveVisionZones = (storeId: string, zones: any[]) =>
  request(API_ENDPOINTS.visionZones(storeId), {
    method: 'POST',
    body: JSON.stringify({ zones }),
  });
